import { useUserContext } from "@/context/UserContext";
import { useMemo } from "react";
import { useTranslation } from "react-i18next";
import useSWR from "swr";

export const specialSubscriptions = {
  PBM: "Program Belajar Mengajar",
};

export const useSubscriptionPlans = () => {
  const groupData = useSWR("/subscriptions/group");
  const { data } = useSWR("/subscriptions");

  const subscriptions = useMemo(() => {
    if (data?.data && groupData.data?.data) {
      const subscriptions = data.data.reduce((p, c) => {
        const group = groupData.data.data.subscription_group.find(
          (m) => m.name === c.group
        );
        if (group) {
          p[c.group] = c;
        }
        return p;
      }, {});

      const lists: any[] = [
        {
          name: "free",
          slug: "free",
          image: "plans-free.svg",
          price: 0,
          prices: [false, false],
          available: [true, true],
        },
      ];

      if (subscriptions.lite) {
        lists.push({
          name: "lite",
          slug: "lite",
          image: "plans-lite.svg",
          price: 1,
          prices: [true, true],
          billed_annually: false,
          with_discount: false,
          available: [false, true],
        });
      }

      if (subscriptions.starter) {
        lists.push({
          name: "Starter",
          slug: "starter",
          image: "plans-starter.svg",
          coming: false,
          price: true,
          prices: [true, true],
          billed_annually: true,
          available: [true, true],
        });
      }

      if (subscriptions.pro) {
        lists.push({
          name: "pro",
          slug: "pro",
          image: "plans-pro.svg",
          mostPopular: true,
          price: true,
          coming: false,
          prices: [true, true],
          billed_annually: true,
          available: [true, true],
        });
      }

      if (subscriptions.ultimate) {
        lists.push({
          name: "ultimate",
          slug: "ultimate",
          image: "plans-ultimate.svg",
          price: true,
          coming: false,
          prices: [true, true],
          billed_annually: true,
          available: [true, true],
        });
      }

      // lists.push({
      //   name: "custom",
      //   slug: "custom",
      //   image: "plans-enterprise.svg",
      //   price: false,
      //   prices: [false, false],
      //   billed_annually: false,
      //   available: [true, true],
      // })

      return lists;
    }
    return [{}, {}, {}, {}];
  }, [data, groupData.data]);

  return subscriptions;
};

export const currencyGroups = {
  idr: { label: "🇮🇩 IDR", tag: "Rp ", locale: "id" },
  usd: { label: "🇺🇸 USD", tag: "$", locale: "en" },
  // eur: { label: "🇪🇺 EU", tag: "€", locale: "en" },
};

export const useFeatures = (customGroups = []) => {
  const groupData = useSWR("/subscriptions/group");

  const { status } = useUserContext();

  const { t } = useTranslation();
  const shopConfig = useSWR("/shop");
  const { data, isLoading } = useSWR("/subscriptions", {
    refreshInterval: 0,
    revalidateOnFocus: false,
  });

  const legacySubs = useSWR(
    status === "authenticated" ? "/user/subscription/legacy" : null
  );

  const configs = useMemo(() => {
    if (data?.data) {
      return data.data.reduce((p, c) => {
        p[c.config.name?.toLowerCase()] = c.config;
        return p;
      }, {});
    }
    return {};
  }, [data]);

  const groups = useMemo(() => {
    if (customGroups.length > 0) {
      return customGroups;
    }

    if (groupData.isLoading) {
      return [];
    }

    const groups = ["free"];

    if (legacySubs?.data?.data) {
      groups.push(...legacySubs.data.data.legacy_group.map((m) => m.name));
    }

    if (groupData.data?.data) {
      groups.push(...groupData.data.data.subscription_group.map((m) => m.name));
    }

    return groups;
  }, [
    configs,
    groupData.data,
    groupData.isLoading,
    customGroups,
    legacySubs.data,
  ]);

  const features = useMemo(() => {
    return [
      {
        name: "access_visit",
        isBeta: false,
        features: groups.map(() => t("unlimited")),
      },
      {
        name: "create_shortener",
        features: groups.map((m) => {
          if (m === "custom") return t("custom");
          return t("x_per_month", { value: configs[m]?.max_create_link });
        }),
      },
      {
        name: "create_shortener_link_name",
        features: groups.map((m) => {
          if (m === "custom") return true;
          return configs[m]?.change_shortname_quota ? 2 : true;
        }),
        isChild: true,
      },
      {
        name: "create_shortener_change_link_name",
        features: groups.map((m) => {
          if (m === "custom") return t("custom");
          return t("x_per_month", { value: configs[m]?.max_change_shortname });
        }),
        isChild: true,
      },
      {
        name: "change_original_url",
        features: groups.map((m) => {
          if (m === "custom") return t("custom");
          if (m === "free") return false;
          if (m === "lite") return false;
          return t("x_per_month", { value: configs[m]?.max_edit_long_url });
        }),
        isChild: true,
      },
      {
        name: "create_microsite",
        features: groups.map((m) => {
          if (m === "custom") return t("custom");
          return t("up_to", { value: configs[m]?.max_microsite });
        }),
      },
      {
        name: "microsite_component_quota",
        isChild: true,
        features: groups.map((m) => {
          if (m === "custom") return t("custom");
          return t("up_to", { value: configs[m]?.max_microsite_component });
        }),
      },
      {
        name: "change_microsite_url",
        isChild: true,
        features: groups.map((m) => {
          if (m === "free") return false;
          return true;
        }),
      },
      {
        name: "microsite_meta",
        isChild: true,
        features: groups.map((m) => {
          if (m === "custom") return true;
          return configs[m]?.microsite_meta;
        }),
      },
      {
        name: "disable_powered",
        isChild: true,
        features: groups.map((m) => {
          if (m === "free") return false;
          if (m === "lite") return false;
          return true;
        }),
      },
      {
        name: "microsite_component_lock",
        isChild: true,
        features: groups.map((m) => {
          if (m === "free") return false;
          if (m === "lite") return false;
          return true;
        }),
      },
      {
        name: "locked_microsite",
        isChild: true,
        isNew: true,
        features: groups.map((m) => {
          return configs[m]?.protected_microsite;
        }),
      },
      {
        name: "microsite_component_google_analytics",
        isChild: true,
        isNew: true,
        features: groups.map((m) => {
          return configs[m]?.microsite_ga;
        }),
      },
      {
        name: "microsite_component_facebook_pixel",
        isChild: true,
        isNew: true,
        features: groups.map((m) => {
          return configs[m]?.microsite_fb_pixel;
        }),
      },
      {
        name: "custom_domain_for_microsite",
        isNew: false,
        features: groups.map((m) => {
          return configs[m]?.max_domain > 0;
        }),
      },
      {
        name: "domain_quota",
        isChild: true,
        features: groups.map((m) => {
          if (configs[m]?.max_domain > 0) {
            return t("up_to", { value: configs[m]?.max_domain });
          }
          return false;
        }),
      },
      {
        name: "domain_target_quota",
        isChild: true,
        features: groups.map((m) => {
          if (configs[m]?.max_domain_target > 0) {
            return t("up_to", { value: configs[m]?.max_domain_target });
          }
          return false;
        }),
      },
      {
        name: "custom_subdomain",
        isChild: true,
        features: groups.map((m) => {
          return configs[m]?.use_subdomain;
        }),
      },
      {
        name: "premium_name",
        features: groups.map((m) => {
          if (m === "custom") return t("custom");
          if (m === "free") return false;
          if (m === "lite") return false;
          return t("x_per_month", { value: configs[m]?.max_premium_name });
        }),
      },
      {
        name: "custom_qrcode",
        features: groups.map(() => true),
      },
      {
        name: "custom_qrcode_hide_logo",
        isChild: true,
        features: groups.map((m) => {
          if (m === "free") return false;
          if (m === "lite") return false;
          return true;
        }),
      },
      {
        name: "custom_qrcode_logo",
        isChild: true,
        features: groups.map((m) => {
          if (m === "custom") return t("custom");
          if (m === "free") return false;
          if (m === "lite") return false;
          return t("up_to", { value: configs[m]?.max_qrcode });
        }),
      },
      {
        name: "custom_qrcode_color",
        isChild: true,
        features: groups.map((m) => {
          if (m === "custom") return t("custom");
          if (m === "free") return false;
          if (m === "lite") return false;
          return t("up_to", { value: configs[m]?.max_qrcode });
        }),
      },
      {
        name: "analytics",
        features: groups.map(() => true),
      },
      {
        name: "analytics_lifetime",
        isChild: true,
        features: groups.map((m) => {
          if (m === "custom") return true;
          return configs[m]?.lifetime_visitor;
        }),
      },
      {
        name: "analytics_range",
        isChild: true,
        features: groups.map((m) => {
          if (m === "custom") return t("custom");
          return t("x_until_day", { value: configs[m]?.query_range });
        }),
      },
      {
        name: "analytics_retention",
        isChild: true,
        features: groups.map((m) => {
          if (m === "custom") return t("custom");
          return t("x_until_day", { value: configs[m]?.max_retention });
        }),
      },
      {
        name: "advance_analytics",
        isChild: true,
        features: groups.map((m) => {
          if (m === "custom") return true;
          return configs[m]?.use_advance_analytics;
        }),
      },
      {
        name: "analytics_export_csv",
        isChild: true,
        features: groups.map((m) => {
          return configs[m]?.analytics_export_csv;
        }),
      },

      {
        name: "shop",
        isNew: true,
        disabled: shopConfig?.data?.data?.enabled !== true,
        features: groups.map((m) => {
          return configs[m]?.shop_feature;
        }),
      },
      {
        name: "shop_item",
        isChild: true,
        disabled: shopConfig?.data?.data?.enabled !== true,
        features: groups.map((m) => {
          return t("unlimited");
        }),
      },
      {
        name: "shop_item_webhook",
        isChild: true,
        disabled: shopConfig?.data?.data?.enabled !== true,
        features: groups.map((m) => {
          return configs[m]?.shop_item_webhook;
        }),
      },
      {
        name: "shop_listing",
        isChild: true,
        disabled: shopConfig?.data?.data?.enabled !== true,
        features: groups.map((m) => {
          return t("up_to", { value: configs[m]?.shop_listing });
        }),
      },
      {
        name: "shop_voucher",
        isChild: true,
        disabled: shopConfig?.data?.data?.enabled !== true,
        features: groups.map((m) => {
          const voucher = configs[m]?.shop_voucher;
          return voucher === -1
            ? true
            : typeof voucher === "number"
            ? t("up_to", { value: voucher })
            : false;
        }),
      },
      {
        name: "shop_use_review",
        isChild: true,
        disabled: shopConfig?.data?.data?.enabled !== true,
        features: groups.map((m) => {
          return configs[m]?.shop_use_review;
        }),
      },
      {
        name: "shop_settlement",
        isChild: true,
        disabled: shopConfig?.data?.data?.enabled !== true,
        features: groups.map((m) => {
          return "3 " + t("day");
        }),
      },
      {
        name: "shop_service_fee",
        isChild: true,
        disabled: shopConfig?.data?.data?.enabled !== true,
        features: groups.map((m) => {
          return "" + (configs[m]?.shop_service_fee * 100).toString() + "%";
        }),
      },
      {
        name: "shop_withdrawal_fee",
        isChild: true,
        disabled: shopConfig?.data?.data?.enabled !== true,
        features: groups.map((m) => {
          return (
            <>
              {"Rp " +
                Intl.NumberFormat("id").format(configs[m]?.withdrawal_fee) +
                ""}
              <span className="text-foreground/60 text-xs">**</span>
            </>
          );
        }),
      },
      {
        name: "shop_email_notification",
        isChild: true,
        disabled: shopConfig?.data?.data?.enabled !== true,
        features: groups.map((m) => {
          return true;
        }),
      },
      {
        name: "shop_whatsapp_notification",
        isChild: true,
        disabled: shopConfig?.data?.data?.enabled !== true,
        features: groups.map((m) => {
          return configs[m]?.shop_whatsapp_notification;
        }),
      },
      {
        name: "shop_telegram_notification",
        isChild: true,
        disabled: shopConfig?.data?.data?.enabled !== true,
        features: groups.map((m) => {
          return configs[m]?.shop_telegram_notification;
        }),
      },
      {
        name: "shop_webhook_notification",
        isChild: true,
        disabled: shopConfig?.data?.data?.enabled !== true,
        features: groups.map((m) => {
          return configs[m]?.shop_webhook_notification;
        }),
      },
      {
        name: "no_ads",
        features: groups.map((m) => {
          if (m === "free") return false;
          return true;
        }),
      },
      {
        name: "no_ads_on_dashboard",
        isChild: true,
        features: groups.map((m) => {
          if (m === "free") return false;
          return true;
        }),
      },
      {
        name: "no_ads_on_linkplus",
        isChild: true,
        features: groups.map((m) => {
          if (m === "free") return false;
          return true;
        }),
      },
      {
        name: "no_ads_on_protected_link",
        isChild: true,
        features: groups.map((m) => {
          if (m === "free") return false;
          return true;
        }),
      },
      {
        name: "no_ads_on_microsite",
        isChild: true,
        features: groups.map((m) => {
          if (m === "free") return false;
          return true;
        }),
      },
    ];
  }, [t, configs, groups]);

  return { features, groups, isLoading };
};
